import { Component, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { JJamesCardComponent } from 'shared-lib';
import { CreateDeviceGroupRequest, DeviceGroupDto, DeviceGroupService, DeviceGroupType, InfrastructureDto, InfrastructureService } from '../../../../api/gen';
import { firstValueFrom } from 'rxjs';

export interface DeviceGroupSpaceConfigData {
  infrastructure: InfrastructureDto;
}

@Component({
  selector: 'app-device-group-space-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatListModule,
    MatProgressBarModule,
    JJamesCardComponent
  ],
  templateUrl: './device-group-space-config.component.html',
  styleUrls: ['./device-group-space-config.component.scss']
})
export class DeviceGroupSpaceConfigComponent implements OnInit {
  infrastructure: InfrastructureDto;
  configuredDeviceGroups = signal<DeviceGroupDto[]>([]);
  isSaving = signal(false);
  isLoading = signal(true);

  newGroupForm: FormGroup;
  deviceGroupTypes = Object.values(DeviceGroupType);

  constructor(
    private dialogRef: MatDialogRef<DeviceGroupSpaceConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeviceGroupSpaceConfigData,
    private fb: FormBuilder,
    private deviceGroupService: DeviceGroupService,
    private infrastructureService: InfrastructureService,
    private snackBar: MatSnackBar
  ) {
    this.infrastructure = data.infrastructure;
    this.newGroupForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      type: [DeviceGroupType.Gateway, Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadConfiguredDeviceGroups();
  }

  loadConfiguredDeviceGroups(): void {
    this.isLoading.set(true);
    if (this.infrastructure.deviceGroups) {
      this.configuredDeviceGroups.set(this.infrastructure.deviceGroups);
    }
    this.isLoading.set(false);
  }

  async onAddNewGroup(): Promise<void> {
    if (this.newGroupForm.invalid) {
      this.newGroupForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const formValue = this.newGroupForm.value;

    const request: CreateDeviceGroupRequest = {
      infrastructureId: this.infrastructure.id!,
      groupName: formValue.name,
      groupDescription: formValue.description,
      groupType: formValue.type,
    };

    try {
      const newGroup = await firstValueFrom(this.deviceGroupService.deviceGroupsPost(request as any));
      this.configuredDeviceGroups.update(groups => [...groups, newGroup]);
      this.snackBar.open('Device group created and assigned successfully.', 'Close', { duration: 3000 });
      this.newGroupForm.reset({ type: DeviceGroupType.Gateway });
    } catch (error) {
      console.error('Error creating or assigning device group:', error);
      this.snackBar.open('Failed to create or assign device group.', 'Close', { duration: 5000 });
    } finally {
      this.isSaving.set(false);
    }
  }

  async removeGroup(deviceGroupId: string): Promise<void> {
    try {
      await firstValueFrom(this.infrastructureService.infrastructuresInfrastructureIdDeviceGroupsDeviceGroupIdDelete(this.infrastructure.id!, deviceGroupId));
      this.configuredDeviceGroups.update(groups => groups.filter(g => g.id !== deviceGroupId));
      this.snackBar.open('Device group unassigned successfully.', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Error unassigning device group:', error);
      this.snackBar.open('Failed to unassign device group.', 'Close', { duration: 5000 });
    }
  }

  onClose(): void {
    this.dialogRef.close(true); // Pass true to indicate changes were made
  }
}
